import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { 
  ArrowLeft, 
  CheckCircle2, 
  XCircle, 
  Trophy,
  RotateCcw
} from "lucide-react";

const QuizView = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);

  useEffect(() => {
    fetchModule();
  }, [id]);

  const fetchModule = async () => {
    try {
      const response = await axios.get(`${API}/modules/${id}`);
      setModule(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement du quiz");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const quiz = module.quiz;
    if (Object.keys(answers).length < quiz.questions.length) {
      toast.error("Veuillez répondre à toutes les questions");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/progress/${id}/quiz`, {
        module_id: id,
        answers
      });
      setResult(response.data);
      setSubmitted(true);
      
      if (response.data.passed) {
        toast.success("🎉 Félicitations! Vous avez réussi le quiz!");
      } else {
        toast.warning("Score insuffisant. Réessayez!");
      }
    } catch (error) {
      toast.error("Erreur lors de la soumission du quiz");
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setAnswers({});
    setSubmitted(false);
    setResult(null);
    setCurrentQuestion(0);
  };

  const getQuestionResult = (question) => {
    if (!submitted) return null;
    const userAnswer = answers[question.id];
    return userAnswer === question.correct_answer ? "correct" : "incorrect";
  };

  if (loading || !module) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const quiz = module.quiz;
  const progress = ((currentQuestion + 1) / quiz.questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-purple-50 to-cream-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate(`/module/${id}`)}
              data-testid="back-to-module-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au module
            </Button>
            <div className="text-sm font-medium text-gray-600">
              Question {currentQuestion + 1} / {quiz.questions.length}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quiz Header */}
        <div className="mb-8 animate-fadeIn">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {quiz.title}
          </h1>
          <p className="text-gray-600 mb-4">{quiz.description}</p>
          <Progress value={submitted ? 100 : progress} className="h-2" data-testid="quiz-progress-bar" />
        </div>

        {/* Result Card */}
        {submitted && result && (
          <Card className={`mb-8 border-0 shadow-2xl animate-fadeIn overflow-hidden ${ 
            result.passed ? "bg-gradient-to-br from-green-50 to-emerald-50" : "bg-gradient-to-br from-orange-50 to-amber-50"
          }`}>
            <CardContent className="py-8">
              <div className="flex items-center gap-6">
                <div className={`p-6 rounded-2xl shadow-lg ${ 
                  result.passed 
                    ? "bg-gradient-to-br from-green-100 to-emerald-100" 
                    : "bg-gradient-to-br from-orange-100 to-amber-100"
                }`}>
                  {result.passed ? (
                    <Trophy className="w-12 h-12 text-green-600" />
                  ) : (
                    <RotateCcw className="w-12 h-12 text-orange-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-testid="quiz-result-title">
                    {result.passed ? "🎉 Félicitations!" : "💪 Encore un effort!"}
                  </h3>
                  <div className="flex items-center gap-4 mb-3">
                    <div className="text-4xl font-bold text-orange-600" data-testid="quiz-score">
                      {result.score.toFixed(0)}%
                    </div>
                    <div className="text-gray-700">
                      <div className="font-semibold">{result.correct}/{result.total} réponses correctes</div>
                      <div className="text-sm text-gray-600">
                        {result.passed ? "✅ Module validé!" : "❌ Minimum requis: 80%"}
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700">
                    {result.passed
                      ? "Excellent travail! Vous avez validé ce module. Passez au suivant pour continuer votre formation."
                      : "Vous y êtes presque! Relisez le contenu et réessayez. Vous pouvez le faire!"}
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  {!result.passed && (
                    <Button
                      onClick={handleRetry}
                      className="btn-primary text-white px-6 py-3"
                      data-testid="retry-quiz-button"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Réessayer
                    </Button>
                  )}
                  <Button
                    onClick={() => navigate("/dashboard")}
                    variant="outline"
                    className="px-6 py-3"
                    data-testid="return-dashboard-button"
                  >
                    Retour au tableau de bord
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Questions */}
        <div className="space-y-6">
          {quiz.questions.map((question, qIndex) => {
            const questionResult = getQuestionResult(question);
            const isVisible = !submitted || submitted;

            return (
              <Card
                key={question.id}
                className={`border-0 shadow-lg animate-fadeIn hover:shadow-xl transition-shadow ${
                  questionResult === "correct" ? "border-l-4 border-l-green-500 bg-green-50/30" :
                  questionResult === "incorrect" ? "border-l-4 border-l-red-500 bg-red-50/30" : "bg-white"
                }`}
                style={{ display: isVisible ? "block" : "none" }}
                data-testid={`question-${qIndex}-card`}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                        questionResult === "correct" ? "bg-green-100 text-green-700" :
                        questionResult === "incorrect" ? "bg-red-100 text-red-700" :
                        "bg-gradient-to-br from-orange-100 to-purple-100 text-orange-700"
                      }`}>
                        {qIndex + 1}
                      </div>
                      <CardTitle className="text-lg flex-1 leading-relaxed pt-1">
                        {question.question}
                      </CardTitle>
                    </div>
                    {questionResult && (
                      <div>
                        {questionResult === "correct" ? (
                          <div className="p-2 bg-green-100 rounded-full">
                            <CheckCircle2 className="w-6 h-6 text-green-600" data-testid={`question-${qIndex}-correct-icon`} />
                          </div>
                        ) : (
                          <div className="p-2 bg-red-100 rounded-full">
                            <XCircle className="w-6 h-6 text-red-600" data-testid={`question-${qIndex}-incorrect-icon`} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <RadioGroup
                    value={answers[question.id] || ""}
                    onValueChange={(value) => {
                      if (!submitted) {
                        setAnswers({ ...answers, [question.id]: value });
                      }
                    }}
                    disabled={submitted}
                    className="space-y-3"
                  >
                    {question.options.map((option, oIndex) => {
                      const optionId = String.fromCharCode(65 + oIndex);
                      const isSelected = answers[question.id] === optionId;
                      const isCorrectAnswer = optionId === question.correct_answer;
                      
                      let optionClass = "quiz-option p-4 rounded-lg border-2 border-gray-200 ";
                      if (submitted) {
                        if (isCorrectAnswer) {
                          optionClass += "correct ";
                        } else if (isSelected && !isCorrectAnswer) {
                          optionClass += "incorrect ";
                        }
                      } else if (isSelected) {
                        optionClass += "selected ";
                      }

                      return (
                        <div key={oIndex} className={optionClass} data-testid={`question-${qIndex}-option-${oIndex}`}>
                          <div className="flex items-center space-x-3">
                            <RadioGroupItem
                              value={optionId}
                              id={`${question.id}-${optionId}`}
                              disabled={submitted}
                            />
                            <Label
                              htmlFor={`${question.id}-${optionId}`}
                              className="flex-1 cursor-pointer font-normal"
                            >
                              <span className="font-semibold mr-2">{optionId}.</span>
                              {option}
                            </Label>
                            {submitted && isCorrectAnswer && (
                              <CheckCircle2 className="w-5 h-5 text-green-600" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </RadioGroup>
                  {submitted && question.explanation && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-gray-700">
                        <strong className="text-blue-900">Explication:</strong> {question.explanation}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Submit Button */}
        {!submitted && (
          <div className="mt-8 flex justify-center">
            <Button
              onClick={handleSubmit}
              disabled={Object.keys(answers).length < quiz.questions.length || loading}
              className="btn-secondary text-white px-8 py-6 text-lg"
              data-testid="submit-quiz-button"
            >
              {loading ? "Soumission..." : "Soumettre le quiz"}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default QuizView;
